// Cliente 360 (Ola 3, chunk 6) — pestaña Excepciones: tabla paginada de las
// excepciones del cliente (endpoint /admin/excepciones con filtro por
// idCliente). El detalle se abre en overlay (ExcepcionDetailModal) para no
// perder el contexto del cliente.
import { useState } from 'react';
import { Button, Tooltip } from 'antd';
import { EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { DataTable } from '../../../components/DataTable';
import { EmptyState } from '../../../components/EmptyState';
import { useHasPermission } from '../../../auth/useHasPermission';
import { Permission } from '@aid/shared';
import { ExcepcionDetailModal } from '../../excepciones/components/ExcepcionDetailModal';
import { ExcepcionCreateModal } from '../../excepciones/components/ExcepcionCreateModal';
import { useExcepcionesList } from '../../excepciones/api/useExcepciones';
import { excepcionAmount } from '../../excepciones/lib/excepcionAmount';
import { formatCurrency } from '../../../lib/format';
import { paginationProps } from '../../../lib/paginacion';
import { serverSortProps, useServerPaginacion } from '../../../lib/server-sort';
import { useT } from '../../../locales';
import dayjs from 'dayjs';
import type { CobroExcepcionResponse } from '@aid/shared';

const DATE_FORMAT = 'DD/MM/YYYY';

interface ClienteExcepcionesListProps {
  clienteId: number;
}

export function ClienteExcepcionesList({ clienteId }: ClienteExcepcionesListProps) {
  const t = useT();
  const { page, pageSize, sort, handleChange } = useServerPaginacion<CobroExcepcionResponse>();
  const canCreate = useHasPermission(Permission.EXCEPCIONES_CREATE);
  const [excepcionId, setExcepcionId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading, error, refetch } = useExcepcionesList({
    idCliente: clienteId,
    limit: pageSize,
    offset: (page - 1) * pageSize,
    ...sort,
  });

  const createAction = canCreate ? (
    <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
      {t('pages.excepciones.create')}
    </Button>
  ) : undefined;

  const columns = [
    {
      title: t('common.id'),
      key: 'id',
      ...serverSortProps('id', sort),
      width: 130,

      render: (_: unknown, record: CobroExcepcionResponse) =>
        t('pages.clientes.actividad.excepcionId', { id: record.id }),
    },
    {
      title: t('pages.excepciones.motivo'),
      key: 'motivo',
      render: (_: unknown, record: CobroExcepcionResponse) => record.motivo,
      ellipsis: true,
    },
    {
      title: t('pages.excepciones.valor'),
      key: 'valor',
      ...serverSortProps('valor', sort),
      render: (_: unknown, record: CobroExcepcionResponse) => {
        const amount = excepcionAmount(record);
        return amount ? formatCurrency(amount) : '-';
      },
    },
    {
      title: t('pages.excepciones.usuario'),
      key: 'usuario',
      render: (_: unknown, record: CobroExcepcionResponse) =>
        record.usuario ? (record.usuario.username ?? record.usuario.email) : record.idUsuario,
    },
    {
      title: t('pages.excepciones.createdAt'),
      key: 'fechaCreacion',
      ...serverSortProps('fechaCreacion', sort),
      render: (_: unknown, record: CobroExcepcionResponse) =>
        dayjs(record.fechaCreacion).format(DATE_FORMAT),
    },
    {
      title: t('pages.clientes.actions'),
      key: 'actions',
      render: (_: unknown, record: CobroExcepcionResponse) => (
        <Tooltip title={t('pages.clientes.ver')}>
          <Button
            type="text"
            icon={<EyeOutlined />}
            aria-label={t('pages.clientes.ver')}
            onClick={() => setExcepcionId(record.id)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      {error ? (
        <EmptyState compact title={t('pages.excepciones.errorLoad')} />
      ) : (!data?.data || data.data.length === 0) && !isLoading ? (
        <>
          <EmptyState
            compact
            title={t('pages.clientes.actividad.sinExcepciones')}
            description={t('pages.clientes.actividad.sinExcepcionesDesc')}
            action={createAction}
          />
          <ExcepcionCreateModal
            open={createOpen}
            onClose={() => setCreateOpen(false)}
            onSuccess={refetch}
            preselectedClienteId={clienteId}
          />
        </>
      ) : (
        <>
          {canCreate && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
              {createAction}
            </div>
          )}
          <DataTable<CobroExcepcionResponse>
            columns={columns}
            dataSource={data?.data}
            rowKey="id"
            loading={isLoading}
            pagination={paginationProps(t, { page, pageSize, total: data?.total ?? 0 })}
            onChange={handleChange}
          />
          <ExcepcionCreateModal
            open={createOpen}
            onClose={() => setCreateOpen(false)}
            onSuccess={refetch}
            preselectedClienteId={clienteId}
          />
        </>
      )}
      {excepcionId !== null && (
        <ExcepcionDetailModal id={excepcionId} onClose={() => setExcepcionId(null)} />
      )}
    </div>
  );
}
